/**
 * Cart Drawer Tier Reminder (1–2 items)
 * - When cart drawer (right sidebar) opens, show a prominent reminder:
 *   "Add 1 more item to unlock 10% off + free shipping" (or "Add 2 more...")
 * - Desktop: refreshes/appears on hover over Checkout
 * - Mobile: first tap on Checkout shows reminder (blocks once), second tap proceeds
 *
 * Notes:
 * - Theme code does NOT run on /checkout. This runs before redirect.
 * - You must enforce the actual 10% + free shipping in Shopify Discounts/Shipping rules.
 */

(function () {
  const THRESHOLD_QTY = 3;
  const SHOW_MIN = 1;
  const SHOW_MAX = 2;

  // How long to "block" checkout on mobile click before allowing user to proceed on the next tap
  const MOBILE_BLOCK_MS = 1200;

  // Banner ID (injected into drawer)
  const BANNER_ID = "pp-tier-drawer-banner";

  // Common cart drawer containers (theme-agnostic)
  const DRAWER_SELECTORS = [
    "#CartDrawer",          // Dawn-like
    "cart-drawer",          // custom element
    "[data-cart-drawer]",   // some themes
    ".cart-drawer",         // generic
    "#cart-drawer",         // generic
    ".drawer.drawer--right" // common
  ];

  // Checkout button selectors commonly used inside drawer
  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href^="/checkout"]',
    '[data-testid*="Checkout"]',
    '[data-testid*="checkout"]',
    '[class*="checkout"] a[href^="/checkout"]',
    '[class*="checkout"] button',
    'button[type="submit"][name="checkout"]'
  ];

  // Preferred injection targets (drawer footer / actions area)
  const INJECT_TARGET_SELECTORS = [
    ".cart-drawer__footer",
    ".drawer__footer",
    ".cart-drawer-footer",
    ".cart__footer",
    "[data-cart-drawer-footer]",
    "footer"
  ];

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Cart fetch failed");
    return res.json();
  }

  function computeMessage(itemCount) {
    const remaining = THRESHOLD_QTY - itemCount;
    if (remaining <= 0) return null;

    if (remaining === 1) {
      return "Add just 1 more item to get 10% off your entire order and free shipping.";
    }
    return `Add ${remaining} more items to get 10% off your entire order and free shipping.`;
  }

  function isDrawerOpen(drawerEl) {
    if (!drawerEl) return false;

    const ariaHidden = drawerEl.getAttribute("aria-hidden");
    if (ariaHidden === "false") return true;

    const openAttr = drawerEl.getAttribute("open");
    if (openAttr !== null) return true;

    const cls = drawerEl.className || "";
    if (typeof cls === "string" && (cls.includes("is-open") || cls.includes("active") || cls.includes("open"))) {
      return true;
    }

    const rect = drawerEl.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function findDrawer() {
    for (const sel of DRAWER_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function findCheckoutButton(scopeEl) {
    const scope = scopeEl || document;
    for (const sel of CHECKOUT_SELECTORS) {
      const el = scope.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function findInjectTarget(drawerEl) {
    if (!drawerEl) return null;

    for (const sel of INJECT_TARGET_SELECTORS) {
      const el = drawerEl.querySelector(sel);
      if (el) return el;
    }

    // Fallback: inject near checkout button parent
    const checkout = findCheckoutButton(drawerEl);
    if (checkout && checkout.parentElement) return checkout.parentElement;

    return drawerEl;
  }

  function ensureBanner(drawerEl) {
    let banner = drawerEl.querySelector(`#${BANNER_ID}`);
    if (banner) return banner;

    banner = document.createElement("div");
    banner.id = BANNER_ID;

    // BIGGER, HIGH-CONTRAST CALLOUT STYLE
    banner.style.cssText = [
      "margin: 14px 0 16px",
      "padding: 16px 18px",
      "border: 2px solid #000",
      "border-radius: 14px",
      "background: #fff3cd",
      "color: #000",
      "font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "font-size: 15.5px",
      "font-weight: 600",
      "line-height: 1.45",
      "display: none",
      "box-shadow: 0 6px 14px rgba(0,0,0,.12)"
    ].join(";");

    // Headline + message (emoji can be removed if you want it ultra-clinical)
    banner.innerHTML = `
      <div style="font-size:17px;font-weight:800;margin-bottom:6px;">
        Unlock 10% Off + Free Shipping
      </div>
      <div id="pp-tier-drawer-text"></div>
    `;

    const target = findInjectTarget(drawerEl);
    target.prepend(banner);

    return banner;
  }

  function showBanner(drawerEl, message) {
    const banner = ensureBanner(drawerEl);
    const text = banner.querySelector("#pp-tier-drawer-text");
    if (text) text.textContent = message;
    banner.style.display = "block";
  }

  function hideBanner(drawerEl) {
    if (!drawerEl) return;
    const banner = drawerEl.querySelector(`#${BANNER_ID}`);
    if (banner) banner.style.display = "none";
  }

  function isMobileish() {
    return window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  }

  function bindCheckoutInteractions(drawerEl) {
    const checkout = findCheckoutButton(drawerEl);
    if (!checkout) return;

    if (checkout.dataset.ppTierBound === "1") return;
    checkout.dataset.ppTierBound = "1";

    // Desktop: show/refresh reminder on hover
    checkout.addEventListener("mouseenter", async () => {
      try {
        const cart = await getCart();
        const itemCount = Number(cart.item_count || 0);

        if (itemCount >= SHOW_MIN && itemCount <= SHOW_MAX) {
          const msg = computeMessage(itemCount);
          if (msg) showBanner(drawerEl, msg);
        } else {
          hideBanner(drawerEl);
        }
      } catch (e) {
        // ignore
      }
    });

    // Mobile: first tap shows reminder and blocks once; second tap proceeds
    checkout.addEventListener(
      "click",
      async (ev) => {
        try {
          const cart = await getCart();
          const itemCount = Number(cart.item_count || 0);

          if (itemCount >= SHOW_MIN && itemCount <= SHOW_MAX) {
            const msg = computeMessage(itemCount);
            if (!msg) return;

            if (isMobileish() && checkout.dataset.ppTierPrimed !== "1") {
              ev.preventDefault();
              ev.stopPropagation();

              showBanner(drawerEl, msg);

              // Prime checkout: allow next click to proceed
              checkout.dataset.ppTierPrimed = "1";
              setTimeout(() => {
                // If they never click again, reset primed
                if (checkout.dataset.ppTierPrimed === "1") {
                  delete checkout.dataset.ppTierPrimed;
                }
              }, MOBILE_BLOCK_MS);

              return;
            } else {
              // Desktop click OR second mobile click
              showBanner(drawerEl, msg);
            }
          }
        } catch (e) {
          // fail open
        }
      },
      { capture: true }
    );
  }

  async function updateDrawerUI(drawerEl) {
    try {
      const cart = await getCart();
      const itemCount = Number(cart.item_count || 0);

      if (itemCount >= SHOW_MIN && itemCount <= SHOW_MAX) {
        const msg = computeMessage(itemCount);
        if (msg) showBanner(drawerEl, msg);
      } else {
        hideBanner(drawerEl);
      }

      bindCheckoutInteractions(drawerEl);
    } catch (e) {
      // ignore
    }
  }

  function watch() {
    let lastOpen = false;

    // Polling loop handles themes where drawer open state is not observable cleanly
    setInterval(() => {
      const drawer = findDrawer();
      if (!drawer) return;

      const open = isDrawerOpen(drawer);

      // On open transition, update UI once
      if (open && !lastOpen) {
        updateDrawerUI(drawer);
      }

      // While open, keep binding alive (drawers often re-render)
      if (open) {
        bindCheckoutInteractions(drawer);
      }

      lastOpen = open;
    }, 400);

    // MutationObserver catches re-renders, ensuring checkout binding persists
    const mo = new MutationObserver(() => {
      const drawer = findDrawer();
      if (drawer && isDrawerOpen(drawer)) {
        bindCheckoutInteractions(drawer);
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  watch();
})();
